import { apiRequest } from './axiosConfig';
import {
  AdminImageStyle,
  ImageStyleCategory,
  ImageStyleCreateRequest,
  ImageStyleUpdateRequest,
  ImageStyleCategoryCreateRequest,
  ImageStyleCategoryUpdateRequest,
} from './adminImageStyleService';

// Re-export types for convenience (same structure as image styles)
export type { AdminImageStyle as AdminMaterialStyle };
export type { ImageStyleCategory as MaterialStyleCategory };

interface MaterialStylesResponse {
  success: boolean;
  data: AdminImageStyle[];
}

interface MaterialStyleResponse {
  success: boolean;
  data: AdminImageStyle;
  message?: string;
}

interface MaterialStyleDeleteResponse {
  success: boolean;
  message: string;
}

interface CategoriesResponse {
  success: boolean;
  data: ImageStyleCategory[];
}

interface CategoryResponse {
  success: boolean;
  data: ImageStyleCategory;
  message?: string;
}

interface CategoryDeleteResponse {
  success: boolean;
  message: string;
}

interface ImageUploadResponse {
  success: boolean;
  message: string;
  data: {
    path: string;
    url: string;
    disk: string;
  };
}

interface GetImageUrlResponse {
  success: boolean;
  data: {
    url: string;
  };
}

const adminMaterialStyleService = {
  // ========================================
  // Material Style CRUD
  // ========================================

  async getAllStyles(): Promise<AdminImageStyle[]> {
    const response = await apiRequest.get<MaterialStylesResponse>('admin/material-styles');
    return response.data;
  },

  async getStyle(id: number): Promise<AdminImageStyle> {
    const response = await apiRequest.get<MaterialStyleResponse>(`admin/material-styles/${id}`);
    return response.data;
  },

  async createStyle(data: ImageStyleCreateRequest): Promise<AdminImageStyle> {
    const response = await apiRequest.post<MaterialStyleResponse>('admin/material-styles', data);
    return response.data;
  },

  async updateStyle(id: number, data: ImageStyleUpdateRequest): Promise<AdminImageStyle> {
    const response = await apiRequest.put<MaterialStyleResponse>(`admin/material-styles/${id}`, data);
    return response.data;
  },

  async deleteStyle(id: number): Promise<void> {
    await apiRequest.delete<MaterialStyleDeleteResponse>(`admin/material-styles/${id}`);
  },

  async toggleStyle(id: number): Promise<AdminImageStyle> {
    const response = await apiRequest.put<MaterialStyleResponse>(`admin/material-styles/${id}/toggle`);
    return response.data;
  },

  async updateOrder(styles: { id: number; sort_order: number }[]): Promise<void> {
    await apiRequest.put('admin/material-styles/order', { styles });
  },

  // ========================================
  // Material Style Category CRUD
  // ========================================

  async getCategories(): Promise<ImageStyleCategory[]> {
    const response = await apiRequest.get<CategoriesResponse>('admin/material-style-categories');
    return response.data;
  },

  async getActiveCategories(): Promise<ImageStyleCategory[]> {
    const response = await apiRequest.get<CategoriesResponse>('admin/material-style-categories/active');
    return response.data;
  },

  async getCategory(id: number): Promise<ImageStyleCategory> {
    const response = await apiRequest.get<CategoryResponse>(`admin/material-style-categories/${id}`);
    return response.data;
  },

  async createCategory(data: ImageStyleCategoryCreateRequest): Promise<ImageStyleCategory> {
    const response = await apiRequest.post<CategoryResponse>('admin/material-style-categories', data);
    return response.data;
  },

  async updateCategory(id: number, data: ImageStyleCategoryUpdateRequest): Promise<ImageStyleCategory> {
    const response = await apiRequest.put<CategoryResponse>(`admin/material-style-categories/${id}`, data);
    return response.data;
  },

  async deleteCategory(id: number): Promise<void> {
    await apiRequest.delete<CategoryDeleteResponse>(`admin/material-style-categories/${id}`);
  },

  async toggleCategory(id: number): Promise<ImageStyleCategory> {
    const response = await apiRequest.put<CategoryResponse>(`admin/material-style-categories/${id}/toggle`);
    return response.data;
  },

  async updateCategoryOrder(categories: { id: number; sort_order: number }[]): Promise<void> {
    await apiRequest.put('admin/material-style-categories/order', { categories });
  },

  // ========================================
  // Image Upload
  // ========================================

  async uploadImage(file: File, type: 'reference' | 'thumbnail'): Promise<{ path: string; url: string }> {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('type', type);

    const response = await apiRequest.post<ImageUploadResponse>(
      'admin/material-styles/upload-image',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return {
      path: response.data.path,
      url: response.data.url,
    };
  },

  async getImageUrl(path: string): Promise<string> {
    const response = await apiRequest.post<GetImageUrlResponse>('admin/material-styles/get-image-url', { path });
    return response.data.url;
  },
};

export default adminMaterialStyleService;
